<script setup lang="ts">
import { computed } from 'vue'
import { AgGridVue } from 'ag-grid-vue3'
import type { ICellRendererParams, ValueFormatterParams, SelectionChangedEvent } from 'ag-grid-community'
import { useRouter } from 'vue-router'
import { type Ticket } from '@/stores/useTicketStore'
import TicketCheckDetail from '@/components/features/tickets/TicketCheckDetail.vue'
import TicketRefLink from '@/components/features/tickets/TicketRefLink.vue'
import { markRaw } from 'vue'

interface Props {
  tickets: Ticket[]
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
})

const emit = defineEmits<{
  selectionChanged: [tickets: Ticket[]]
}>()

const router = useRouter()

function handleOpenTicket(ticketId: string) {
  router.push(`/support-interne/${ticketId}`)
}

const statusTranslations: Record<string, string> = {
  'NEW': 'NOUVEAU',
  'OPEN': 'OUVERT',
  'WAITING': 'EN ATTENTE',
  'PAUSED': 'EN PAUSE',
  'RESOLVED': 'RÉSOLU',
  'CLOSED': 'FERMÉ'
}

const statusColors: Record<string, string> = {
  'NEW': 'bg-blue-100 text-blue-800 border-blue-200',
  'OPEN': 'bg-green-100 text-green-800 border-green-200',
  'WAITING': 'bg-orange-100 text-orange-800 border-orange-200',
  'PAUSED': 'bg-gray-100 text-gray-800 border-gray-200',
  'CLOSED': 'bg-red-100 text-red-800 border-red-200',
  'RESOLVED': 'bg-teal-100 text-teal-800 border-teal-200'
}

const columnDefs = computed(() => [
  {
    field: 'internalReference',
    headerName: 'Référence',
    flex: 2,
    minWidth: 120,
    cellRenderer: markRaw(TicketRefLink)
  },
  {
    field: 'title',
    headerName: 'Titre',
    flex: 3,
    minWidth: 200,
  },
  {
    field: 'description',
    headerName: 'Description',
    flex: 4,
    minWidth: 300,
    wrapText: true,
    autoHeight: true
  },
  {
    field: 'ticketStatus',
    headerName: 'Statut',
    flex: 2,
    minWidth: 150,
    cellRenderer: (params: ICellRendererParams) => {
      const status = params.value || '-'
      const translatedStatus = statusTranslations[status] || status
      const colorClass = statusColors[status] || 'bg-primary/10 text-primary border-primary/30'
      return `<span class="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${colorClass}">${translatedStatus}</span>`
    }
  },
  {
    field: 'dateCreated',
    headerName: 'Date de création',
    flex: 2,
    minWidth: 150,
    valueFormatter: (params: ValueFormatterParams) => params.value ? new Date(params.value).toLocaleString('fr-FR') : '-'
  },
  {
    field: 'dateUpdated',
    headerName: 'Date de mise à jour',
    flex: 2,
    minWidth: 150,
    cellRenderer: (params: ICellRendererParams) => params.value && params.value !== params.data.dateCreated ?
      new Date(params.value).toLocaleString('fr-FR')
      : '<span class="flex items-center justify-center gap-2 h-full">-</span>'
  },
  {
    headerName: '',
    filter: false,
    cellRenderer: markRaw(TicketCheckDetail),
    cellRendererParams: {
      onClick: handleOpenTicket
    }
  }
])

function handleSelectionChanged(event: SelectionChangedEvent) {
  emit('selectionChanged', event.api.getSelectedRows())
}

</script>

<template>
  <div class="w-full ag-theme-alpine h-[600px]">
    <ag-grid-vue
      class="w-full h-full"
      :rowData="props.tickets"
      :columnDefs="columnDefs"
      :pagination="true"
      :paginationPageSize="20"
      rowSelection="multiple"
      @selection-changed="handleSelectionChanged"
    />
  </div>

</template>

<style scoped></style>
